import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateVendorRequest,
  UpdateVendorRequest,
  Vendor,
  VendorDetailResponse,
  VendorsListResponse,
} from '@stfors/shared-types';
import { api } from '../client';

// Vendors are admin-managed metadata; they don't change minute-by-minute.
// Cache aggressively but invalidate on every mutation so a fresh add
// shows up immediately in the picker.
const STALE_MS = 5 * 60 * 1000;

export function useVendors(searchQuery?: string) {
  const q = (searchQuery ?? '').trim();
  const qs = q.length > 0 ? `?q=${encodeURIComponent(q)}` : '';
  return useQuery({
    queryKey: ['vendors', { q: q || null }],
    queryFn: ({ signal }) =>
      api.get<VendorsListResponse>(`/api/vendors${qs}`, signal),
    staleTime: STALE_MS,
  });
}

export function useVendor(id: number | null) {
  return useQuery({
    enabled: id !== null && id > 0,
    queryKey: ['vendor', id],
    queryFn: ({ signal }) =>
      api.get<VendorDetailResponse>(`/api/vendors/${id ?? 0}`, signal),
  });
}

export function useCreateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVendorRequest) =>
      api.post<VendorDetailResponse>('/api/vendors', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export interface UpdateVendorInput extends UpdateVendorRequest {
  id: number;
}

export function useUpdateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateVendorInput) =>
      api.patch<VendorDetailResponse>(`/api/vendors/${id}`, patch),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendors'] });
      void qc.invalidateQueries({ queryKey: ['vendor', vars.id] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
    },
  });
}

export function useDeleteVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/vendors/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendors'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
      void qc.invalidateQueries({ queryKey: ['person'] });
      void qc.invalidateQueries({ queryKey: ['organization'] });
      void qc.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

// Display helper — API `name` is already DBA-preferred; keep this for callers.
export function vendorDisplayName(v: Pick<Vendor, 'name' | 'dba_name'>): string {
  return v.name;
}
